import { useState } from "react";
import { useTranslation } from "react-i18next";
import createId from "../../../utils/createId";

import { systemLabelKey } from "../../../catalogs/characterSystemCatalog";
import Skill from "../../../models/Skill";
import AddSkillModal from "./AddSkillModal";

const SkillsList = ({ skills, setSkills, characterLevel = 0, skillRankCapacity, spentSkillRanks, onRoll, getTotalBonus }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const emptySkill = () => new Skill({ id: "", name: "", modifier: 0, abilityId: "strength", playerSkillPoints: 0, isClassSkill: false });
  const [formData, setFormData] = useState(emptySkill());
  const skillName = (skill) => skill.catalogId ? t(systemLabelKey("skills", skill.catalogId)) : skill.name;
  const remainingSkillRanks = skillRankCapacity - spentSkillRanks;
  const hasSkillRankMismatch = remainingSkillRanks !== 0;
  const skillRankStatus = remainingSkillRanks > 0
    ? t("skills.ranksUnderAllocated", { spent: spentSkillRanks, total: skillRankCapacity, remaining: remainingSkillRanks })
    : remainingSkillRanks < 0
      ? t("skills.ranksOverAllocated", { spent: spentSkillRanks, total: skillRankCapacity, excess: Math.abs(remainingSkillRanks) })
      : t("skills.ranksFullyAllocated", { spent: spentSkillRanks, total: skillRankCapacity });
  const handleAddOrEditSkill = (skill) => {
    setSkills(skill.id ? skills.map((current) => current.id === skill.id ? skill : current) : [...skills, { ...skill, id: createId() }]);
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="skills-list">
        <div className="skills-header">
          <h2>{t("skills.title")}</h2>
          <div className="skills-header-actions">
            <span
              className={`skill-rank-counter${hasSkillRankMismatch ? " skill-rank-counter-warning" : ""}`}
              role="status"
              aria-label={skillRankStatus}
              title={skillRankStatus}
            >
              <span>{spentSkillRanks} / {skillRankCapacity}</span>
              {hasSkillRankMismatch && <span className="skill-rank-warning-mark" aria-hidden="true">!</span>}
            </span>
            <button className="add-skill-button" type="button" onClick={() => { setFormData(emptySkill()); setIsModalOpen(true); }}>+</button>
          </div>
        </div>
        <div className="skill-header"><span className="skill-name">{t("skills.name")}</span><span>{t("skills.ability")}</span><span>{t("skills.totalBonus")}</span><span aria-label={t("skills.actions")} /></div>
        {skills.map((skill) => {
          const name = skillName(skill);
          return (
            <div key={skill.id} className="skill" role="button" tabIndex={0} onClick={() => onRoll(skill)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onRoll(skill); } }}>
              <span className="skill-name">{name}</span>
              <span>{t(systemLabelKey("abilities", skill.abilityId))}</span>
              <span>{getTotalBonus(skill)}</span>
              <button aria-label={t("skills.edit", { name })} className="skill-edit-button" title={t("common.edit")} type="button" onClick={(event) => { event.stopPropagation(); setFormData(skill); setIsModalOpen(true); }}>&#8943;</button>
            </div>
          );
        })}
      </div>
      <AddSkillModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddSkill={handleAddOrEditSkill}
        formData={formData}
        setFormData={setFormData}
        maxSkillRanks={characterLevel}
      />
    </>
  );
};

export default SkillsList;
